import React, { lazy, useState, useEffect } from 'react'
import { useIsHelping } from '@fs/zion-user'
import debugLib from '@fs/zion-debug'
import ErrorBoundary from '@fs/zion-error-boundary'
import { useExperiment } from '@fs/zion-experiments'
// TODO:doing a regular import from zion-messages as opposed to a dynamic import. see comments below for more info
import { showTemple, canAddPersonalData } from '@fs/zion-permissions'
// TODO:doing a regular import from zion-messages as opposed to a dynamic import. see comments below for more info
import { getLastMessageAndNotificationTime } from '@fs/zion-messages'

import Header from '../Header2019/Header2019'
import NavigationProvider from '../shared/NavigationProvider'

const AdminMode = lazy(() => import('@fs/zion-admin-mode'))
const debug = debugLib('header2019')

function HeaderWithDataRenderer({ config, storybookOverride = {} }) {
  const isHelping = useIsHelping()
  const [lastNotificationTime, setLastNotificationTime] = useState()
  const [canSeeTemple, setCanSeeTemple] = useState(false)
  const [restrictedTemple, setRestrictedTemple] = useState(false)
  const [portraitUrl, setPortraitUrl] = useState('')
  const [adminRoles, setAdminRoles] = useState([])
  const [activeRole, setActiveRole] = useState('')
  const [userProfileEx] = useExperiment('userProfileEx')

  const headerConfig = {
    ...config,
    lastNotificationTime,
    canSeeTemple,
    restrictedTemple,
    portraitUrl,
    adminRoles,
    activeRole,
    ...storybookOverride,
  }

  // notification count
  useEffect(() => {
    let mounted = true
    const fetchData = async () => {
      if (headerConfig.signedIn) {
        // TODO: doing a dynamic import here isn't likely doing what we want because other places in zion-header we do a regular import
        // of zion-messages, so when webpack builds/chunks the code, it will likely already have zion-permissions in the chunk,
        // so this probably isn't doing what we want. The nx-module-boundaries eslint plugin is giving an error for this. I am
        // commenting out the dynamic import and doing a regular static import until further investigation can occur
        // import('@fs/zion-messages').then(async ({ getLastMessageAndNotificationTime }) => {
        const { latest_notification_time: latestNotificationTime = null } = await getLastMessageAndNotificationTime()
        if (mounted) {
          setLastNotificationTime(latestNotificationTime)
        }
        // })
      }
    }
    // if we're redirecting to the notifications page, we don't need to fetch (hf-inj-react throws a 403 w/CORS error from subdomains)
    if (!headerConfig.redirectNotifications) {
      fetchData()
    }
    return () => {
      mounted = false
    }
  }, [headerConfig.signedIn, setLastNotificationTime, headerConfig.redirectNotifications])

  // temple permissions
  useEffect(() => {
    let mounted = true
    const fetchData = async () => {
      if (headerConfig.signedIn) {
        // TODO: doing a dynamic import here isn't likely doing what we want because other places in zion-header we do a regular import
        // of zion-permissions, so when webpack builds/chunks the code, it will likely already have zion-permissions in the chunk,
        // so this probably isn't doing what we want. The nx-module-boundaries eslint plugin is giving an error for this. I am
        // commenting out the dynamic import and doing a regular static import until further investigation can occur
        // import('@fs/zion-permissions')
        //   .then(({ showTemple, canAddPersonalData }) =>
        Promise.allSettled([showTemple(), canAddPersonalData('FamilyTree')])
          // )
          .then(([showTempleResponse, permissionResponse]) => {
            if (!mounted) return
            if (showTempleResponse.status === 'fulfilled') {
              setCanSeeTemple(showTempleResponse.value)
            }
            if (permissionResponse.status === 'fulfilled') {
              setRestrictedTemple(!permissionResponse.value)
            }
          })
          .catch((error) => {
            console.error('Temple permission error in header:', error)
          })
      }
    }
    fetchData()
    return () => {
      mounted = false
    }
  }, [headerConfig.signedIn])

  // admin mode
  useEffect(() => {
    if (headerConfig.signedIn && !isHelping) {
      // TODO: Use localstorage to prevent some of these calls
      import('@fs/zion-admin-mode')
        .then(({ getRoles, getActiveRole }) => Promise.all([getRoles(), getActiveRole()]))
        .then(([roles, currentRole]) => {
          setAdminRoles(roles)
          setActiveRole(currentRole)
        })
        .catch((error) => {
          debug('Admin Mode Error in Header2010WithData.js:', error)
        })
    }
  }, [headerConfig.signedIn, isHelping])

  // user portrait
  useEffect(() => {
    let mounted = true
    const fetchData = async () => {
      if (headerConfig.signedIn && !isHelping) {
        import('@fs/zion-portrait-service')
          .then(async ({ getPortraitThumb, getProfilePortrait }) => {
            const userTreeImage = await getPortraitThumb(headerConfig.personId)
            const userProfileImage = await getProfilePortrait(headerConfig.cisId)
            return { userTreeImage, userProfileImage }
          })
          .then(({ userTreeImage, userProfileImage }) => {
            if (mounted && userProfileEx && userProfileImage && userProfileImage.avatarUrl.length !== 0) {
              setPortraitUrl(userProfileImage.avatarUrl)
            } else if (mounted && userTreeImage) {
              setPortraitUrl(userTreeImage)
            }
          })
          .catch((error) => debug('Error getting portrait', error))
      }
    }
    fetchData()
    return () => {
      mounted = false
    }
  }, [headerConfig.signedIn, headerConfig.personId, isHelping, headerConfig.cisId, userProfileEx])

  return (
    <>
      {headerConfig.activeRole && <AdminMode roleName={headerConfig.activeRole} />}
      <NavigationProvider
        canSeeTemple={canSeeTemple}
        nonReligiousMode={headerConfig.nonReligiousMode}
        restrictedTemple={restrictedTemple}
        signedIn={headerConfig.signedIn}
      >
        <Header {...headerConfig} {...storybookOverride} />
      </NavigationProvider>
    </>
  )
}

function handleError(error, info) {
  debug('Header2019 Error', error, info)
}
function Fallback() {
  return <span id="header2019-error" />
}

/**
 * Wrapper to get async, lower-priority data for use in Header2019
 * @param {} config
 */
export default function Header2019WithData({ config, storybookOverride }) {
  return (
    <ErrorBoundary errorHandler={handleError} fallbackComponent={Fallback}>
      <HeaderWithDataRenderer config={config} storybookOverride={storybookOverride} />
    </ErrorBoundary>
  )
}
